/* Warehouse Bold mega menu — open/close contract, sliding underline, panel
 * placement, scroll affordances, band edge fades, popular-row paging. Ported from the design
 * reference; views are server-rendered in Liquid, so this file only toggles.
 *
 * Contract (README "Mega menu"):
 * - Click opens; same item again closes; another item switches IN PLACE.
 * - Escape, outside click (incl. the still-interactive header), ✕ close.
 * - Panel anchors to the band's LIVE bottom edge (band is not sticky),
 *   clamped below the sticky header; recomputed on resize; <750 closes.
 * - Reopen must reset scrollTop AFTER unhiding (a reset while display:none
 *   is a no-op and the panel would resume mid-list).
 * - The scroll lock is class-based (body.mega-lock) and independent of any
 *   drawer's lock so the two mechanisms can never fight.
 */
(() => {
  const init = () => {
    const mega = document.getElementById('wbMega');
    const navband = document.getElementById('navband');
    const bandWrap = document.getElementById('navbandWrap');
    if (!mega || !navband) return;

    /* PORTAL: the panel is position:fixed, and any transformed ancestor
       (the base theme's sticky header animates with transforms) would become its
       containing block and strand it off-screen — the README's ancestor-
       chain warning, hit for real. The body is the only safe parent. */
    document.body.appendChild(mega);

    const triggers = [...navband.querySelectorAll('.navcat[data-mega-cat]')];
    const views = triggers.map((b) => document.getElementById(b.getAttribute('aria-controls')));
    const panel = mega.querySelector('.mega-panel');
    const moreBtn = document.getElementById('wbMegaMore');
    const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* The open-indicator pill: one element under the open trigger. First
       appearance positions INSTANTLY then fades (no cross-band flight from a
       stale spot); switching animates left+width; closing fades out. */
    const pill = document.createElement('span');
    pill.className = 'nav-underline';
    navband.appendChild(pill);
    const movePill = (btn) => {
      if (!btn) { pill.style.opacity = '0'; return; }
      const instant = pill.style.opacity !== '1';
      if (instant) pill.style.transition = 'none';
      pill.style.left = btn.offsetLeft + 'px';
      pill.style.width = btn.offsetWidth + 'px';
      if (instant) { void pill.offsetWidth; pill.style.transition = ''; }
      pill.style.opacity = '1';
    };

    let cur = -1;
    let closing = false;
    /* How the OPEN happened, captured at open time — Escape is itself a
       keydown, so the browser flips to keyboard modality before shut()
       runs and :focus-visible would paint a ring on a trigger the user
       clicked with a mouse (Owen 2026-08-09). A keyboard-opened panel
       still returns a visible ring, which is the accessible behaviour. */
    let openedByPointer = false;
    const activeScroll = () => (cur >= 0 ? views[cur].querySelector('[data-mega-scroll]') : null);

    /* Anchor line = the band's live bottom edge, clamped below the sticky
       header (a half-hidden band must not put the panel under it). Height
       cap ≈78vh minus the head row and breathing room. */
    const place = () => {
      const hdr = document.querySelector('.header__top');
      const hdrBottom = hdr ? hdr.getBoundingClientRect().bottom : 0;
      /* Clamped at 0: with the band scrolled off-screen both rects can go
         negative, which would strand the panel above the viewport. */
      const top = Math.max(0, navband.getBoundingClientRect().bottom, hdrBottom);
      mega.style.setProperty('--mega-top', top + 'px');
      const head = cur >= 0 ? views[cur].querySelector('.mega-head') : null;
      const cap = Math.min(window.innerHeight * 0.78, window.innerHeight - top - 24);
      mega.style.setProperty('--mega-maxh', Math.max(240, cap - (head ? head.offsetHeight : 56)) + 'px');
      updateMore();
    };

    /* .has-more drives the bottom fade + chevron; rechecked on scroll
       (rAF-throttled), on every view switch and on every re-place. */
    let moreTick = false;
    const updateMore = () => {
      const sc = activeScroll();
      if (!sc) { panel.classList.remove('has-more'); return; }
      panel.classList.toggle('has-more', sc.scrollHeight - sc.clientHeight - sc.scrollTop > 24);
    };
    views.forEach((v) => {
      v.querySelector('[data-mega-scroll]').addEventListener('scroll', () => {
        if (moreTick) return;
        moreTick = true;
        requestAnimationFrame(() => { moreTick = false; updateMore(); });
      }, { passive: true });
    });
    if (moreBtn) moreBtn.addEventListener('click', () => {
      const sc = activeScroll();
      if (sc) sc.scrollBy({ top: sc.clientHeight * 0.7, behavior: reduced() ? 'auto' : 'smooth' });
    });

    /* ---- popular-row paging ----
       A top-strip row shows whole tiles only: the CSS shares the row's width
       out into --feat-n equal columns (the Kit Builder promo spans two), so
       no column-aligned scroll position cuts a tile off at the edge. The two
       discs in the row's heading page it, by the same rules as the product
       page's add-on strip (wb-addons.js) and the cart drawer's Pair Well With
       row (wb-cart-recs.js):
         · a page is `per` columns = floor((port + gap) / pitch), pitch =
           column + gap — measured on a TILE, never the first child, which is
           the two-column promo in one category;
         · paging is by column index from a remembered target: a relative
           scrollBy resolves against the mid-glide position, so a quick second
           click would compound into the snap and land off-page;
         · Prev from the clamped last page returns to the page boundary before
           it, not "one page back from wherever the clamp stopped";
         · aria-disabled, not disabled — a button that disables itself under
           keyboard focus drops focus to body;
         · offsets are signed by writing direction and clamped;
         · the pair hides itself whenever every tile already fits.
       Every row sits in a hidden view until its category opens; the
       ResizeObserver repaints a row as it gains a width, so the pair is never
       decided from a display:none box. */
    const featOf = new Map();
    mega.querySelectorAll('[data-mega-feat]').forEach((box) => {
      const row = box.querySelector('[data-feat-row]');
      const nav = box.querySelector('[data-feat-nav]');
      const prev = box.querySelector('[data-feat-prev]');
      const next = box.querySelector('[data-feat-next]');
      if (!row || !nav || !prev || !next) return;
      let target = null;
      let settle = null;
      const gap = () => parseFloat(getComputedStyle(row).columnGap) || 16;
      const rtl = () => getComputedStyle(row).direction === 'rtl';
      const maxOff = () => Math.max(row.scrollWidth - row.clientWidth, 0);
      const offset = () => Math.min(Math.max(rtl() ? -row.scrollLeft : row.scrollLeft, 0), maxOff());
      const paint = () => {
        const max = maxOff();
        const x = offset();
        nav.hidden = max <= 1;
        prev.setAttribute('aria-disabled', x <= 1 ? 'true' : 'false');
        next.setAttribute('aria-disabled', x >= max - 1 ? 'true' : 'false');
        if (target !== null && Math.abs(x - target) < 1) target = null;
      };
      const page = (dir, btn) => {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const tile = row.querySelector('.ftile');
        const g = gap();
        const pitch = tile ? tile.getBoundingClientRect().width + g : 0;
        if (!pitch) return;
        const per = Math.max(1, Math.floor((row.clientWidth + g) / pitch + 0.05));
        const max = maxOff();
        const idx = Math.round((target !== null ? target : offset()) / pitch);
        let to;
        if (dir > 0) to = (Math.floor(idx / per) + 1) * per;
        else if (target === null && offset() >= max - 1) to = Math.floor((max - 2) / (per * pitch)) * per;
        else to = (Math.ceil(idx / per) - 1) * per;
        target = Math.min(Math.max(to * pitch, 0), max);
        clearTimeout(settle);
        settle = setTimeout(() => { target = null; }, 900);
        row.scrollTo({ left: rtl() ? -target : target, behavior: reduced() ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => page(-1, prev));
      next.addEventListener('click', () => page(1, next));
      row.addEventListener('scroll', paint, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(paint).observe(row);
      else addEventListener('resize', paint, { passive: true });
      featOf.set(box.closest('.mega-view'), {
        reset: () => {
          clearTimeout(settle);
          target = null;
          row.scrollLeft = 0;
          paint();
        },
      });
    });
    /* Every open and every switch starts the row on its first page, like the
       vertical reset beside it — and for the same reason it must run AFTER
       unhiding (a reset while display:none is a no-op). */
    const resetFeat = () => {
      const f = cur >= 0 ? featOf.get(views[cur]) : null;
      if (f) f.reset();
    };

    const showView = (i) => {
      views.forEach((v, j) => { v.hidden = j !== i; });
      triggers.forEach((b, j) => b.setAttribute('aria-expanded', j === i ? 'true' : 'false'));
      movePill(triggers[i]);
      cur = i;
    };

    const open = (i) => {
      if (closing) return;
      const wasOpen = !mega.hidden;
      if (wasOpen && cur === i) { shut(); return; }
      showView(i);
      const sc = activeScroll();
      if (!wasOpen) {
        mega.hidden = false;
        place(); // needs live layout — after unhide
        sc.scrollTop = 0; // reset is a NO-OP while display:none
        resetFeat();
        updateMore();
        document.documentElement.style.setProperty('--sbw', (window.innerWidth - document.documentElement.clientWidth) + 'px');
        document.body.classList.add('mega-lock');
        /* rAF + timeout BOTH arm the transition (idempotent): rAF alone
           never fires in a hidden tab, leaving the panel open at opacity 0. */
        requestAnimationFrame(() => mega.classList.add('in'));
        setTimeout(() => mega.classList.add('in'), 30);
      } else {
        sc.scrollTop = 0;
        resetFeat();
        place();
      }
    };

    const shut = () => {
      if (mega.hidden || closing) return;
      closing = true;
      mega.classList.remove('in');
      setTimeout(() => {
        mega.hidden = true;
        document.body.classList.remove('mega-lock');
        closing = false;
      }, 190);
      triggers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      /* Focus returns to the trigger for keyboard users; for a pointer-
         opened panel we drop it, so dismissing with Escape after a click
         leaves no ring. */
      const active = document.activeElement;
      if (openedByPointer && active && active.classList && active.classList.contains('navcat')) active.blur();
      movePill(null);
      cur = -1;
    };

    triggers.forEach((b, i) => b.addEventListener('click', (e) => {
      // detail === 0 means the click came from Enter/Space, not a pointer.
      openedByPointer = e.detail > 0;
      open(i);
    }));
    mega.querySelectorAll('[data-mega-close]').forEach((b) => b.addEventListener('click', shut));
    /* Outside click: anything that is neither the panel nor a trigger —
       covers the overlay AND the still-interactive header above the line. */
    document.addEventListener('click', (e) => {
      if (mega.hidden) return;
      if (e.target.closest('.mega-panel') || e.target.closest('.navcat')) return;
      shut();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !mega.hidden) shut(); });
    addEventListener('resize', () => {
      if (mega.hidden) return;
      if (window.innerWidth < 750) { shut(); return; } // band gone — hamburger owns nav
      place();
      if (cur >= 0) movePill(triggers[cur]);
    }, { passive: true });

    /* ---- band overflow affordance ----
       Fades + chevrons only on the side that has more content; nothing at
       full width. rAF AND a 60ms timeout both re-check (the latch clears in
       the check, so whichever runs first wins) — rAF alone is throttled in
       a background tab and the fades would freeze. */
    if (bandWrap) {
      let bandTick = false;
      const bandEdges = () => {
        bandTick = false;
        const max = navband.scrollWidth - navband.clientWidth;
        bandWrap.classList.toggle('has-prev', navband.scrollLeft > 4);
        bandWrap.classList.toggle('has-next', max > 4 && navband.scrollLeft < max - 4);
      };
      navband.addEventListener('scroll', () => {
        if (bandTick) return;
        bandTick = true;
        requestAnimationFrame(bandEdges);
        setTimeout(bandEdges, 60);
      }, { passive: true });
      addEventListener('resize', bandEdges, { passive: true });
      bandWrap.querySelectorAll('.nav-scroll').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = btn.classList.contains('nav-scroll--next') ? 1 : -1;
          navband.scrollBy({ left: dir * navband.clientWidth * 0.7, behavior: reduced() ? 'auto' : 'smooth' });
        });
      });
      bandEdges();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(bandEdges);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
